import { useState, useEffect, useRef } from 'react';

interface ReadTimeIndicatorProps {
  readTime: string;   // e.g. "10 min"
  maxMinutes?: number; // longest post, used to normalise the bar
}

type Tier = 'quick' | 'medium' | 'deep';

const TIER_CONFIG: Record<Tier, { label: string; bar: string; badge: string; dot: string }> = {
  quick:  { label: 'Leitura rápida',        bar: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700',  dot: 'bg-emerald-500' },
  medium: { label: 'Leitura média',          bar: 'bg-amber-400',   badge: 'bg-amber-50 text-amber-700',     dot: 'bg-amber-400'   },
  deep:   { label: 'Leitura aprofundada',    bar: 'bg-orange-400',  badge: 'bg-orange-50 text-orange-700',   dot: 'bg-orange-400'  },
};

function getTier(minutes: number): Tier {
  if (minutes <= 9)  return 'quick';
  if (minutes <= 12) return 'medium';
  return 'deep';
}

export default function ReadTimeIndicator({ readTime, maxMinutes = 15 }: ReadTimeIndicatorProps) {
  const [animated, setAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const minutes = parseInt(readTime, 10) || 0;
  const pct     = Math.min(100, Math.round((minutes / maxMinutes) * 100));
  const tier    = getTier(minutes);
  const cfg     = TIER_CONFIG[tier];

  // Animate bar into view the first time the card enters the viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimated(true); },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="space-y-2">
      {/* Row: clock + time + badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-time-line text-sm text-gray-400"></i>
          </div>
          <span className="font-body text-xs text-gray-500">
            {readTime} de leitura
          </span>
        </div>

        <span className={`inline-flex items-center gap-1 font-body text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.badge}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
          {cfg.label}
        </span>
      </div>

      {/* Progress track */}
      <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ease-out ${cfg.bar}`}
          style={{ width: animated ? `${pct}%` : '0%' }}
        />
      </div>
    </div>
  );
}
